/* eslint-disable jsx-a11y/media-has-caption */
"use client";
import type { ChakraProps } from "@chakra-ui/react";
import { Box } from "@chakra-ui/react";
import { Logger } from "lib/logger";
import debounce from "lodash.debounce";
import Head from "next/head";
import type { ImageProps } from "next/image";
import Image from "next/image";
import type { HTMLProps } from "react";
import React, { useEffect, useRef, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useInView } from "react-intersection-observer";
import { useSegment } from "tracking";
import { removeNullField } from "tracking/utils/nullFields";
import { trackVideo } from "utils/gtm/video";
import type { VideoJsPlayer, VideoJsPlayerOptions } from "video.js";
import "video.js/dist/video-js.css";
import styles from "./VideoPlayer.legacy.module.css";

const videoStyles = Object.keys(styles)
  .map((t) => styles[t])
  .join(" ");

interface Props extends ChakraProps {
  coverImageProps: ImageProps;
  coverImageContainerProps?: ChakraProps;
  options: VideoJsPlayerOptions;
  videoProps?: HTMLProps<HTMLVideoElement>;
  videoPosition?: string | undefined;
  autoPlayWhenScrolledIntoView?: boolean;
  pauseWhenNotInView?: boolean;
  muted?: boolean;
  description?: string;
  isPlaceholderEnabled?: boolean;
  isThumbnail?: boolean;
}

interface PosterProps extends ChakraProps {
  imgProps?: ImageProps;
}

const Poster: React.FC<PosterProps> = ({ imgProps, ...otherProps }) => (
  <Box
    position="absolute"
    top="0"
    left="0"
    bottom="0"
    right="0"
    w="100%"
    h="100%"
    cursor="pointer"
    {...otherProps}
  >
    {imgProps?.src && (
      <Image
        style={{ objectFit: imgProps?.style?.objectFit ?? "cover" }}
        title="Video Placeholder"
        {...imgProps}
        fill
        sizes="100vw"
      />
    )}
  </Box>
);

const VideoPlayerComponent: React.FC<Props> = ({
  options,
  videoProps,
  coverImageProps,
  coverImageContainerProps,
  videoPosition,
  autoPlayWhenScrolledIntoView = false,
  pauseWhenNotInView = false,
  muted = false,
  description,
  isPlaceholderEnabled = true,
  isThumbnail = false,
  ...otherProps
}) => {
  const { track } = useSegment();

  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.75
  });

  const inViewRef = useRef(inView);
  useEffect(() => {
    inViewRef.current = inView;
  }, [inView]);

  const { inView: preloadInView } = useInView({
    triggerOnce: true,
    rootMargin: "500px 0px 500px 0px", // Start preloading when within 500px of the viewport
    threshold: 0
  });

  const [isVideoLoaded, setVideoLoaded] = useState(false);
  const [isCoverImageHidden, setCoverImageHidden] = useState(true);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const isAutoMuted = useRef(muted);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoPlayerRef = useRef<VideoJsPlayer>();
  const isProgrammaticPause = useRef<boolean>(false);
  const bufferStartTimeRef = useRef<number | null>(Date.now());

  useEffect(() => {
    let player: VideoJsPlayer;

    const loadVideo = async () => {
      const VideoJS = (await import("video.js")).default;
      if (!videoRef.current) return undefined;

      player = VideoJS(videoRef.current, {
        ...options,
        autoplay: false,
        preload: "metadata"
      });
      const loadingSpinner = player.getChild("LoadingSpinner");
      if (loadingSpinner) {
        loadingSpinner.hide();
      }
      videoPlayerRef.current = player;

      player.on("ready", () => {
        setIsReady(true);
        // We don't want the big button to flash before autoplay
        if (autoPlayWhenScrolledIntoView) {
          const bigPlayButton = player.getChild("BigPlayButton");
          if (bigPlayButton) {
            bigPlayButton.hide();
          }
        }
      });

      player.on("pause", () => {
        // after pause happens we do want to show the big play button
        if (autoPlayWhenScrolledIntoView) {
          const bigPlayButton = player.getChild("BigPlayButton");
          if (bigPlayButton) {
            bigPlayButton.show();
          }
        }
      });

      player.on("canplay", () => {
        if (bufferStartTimeRef.current && videoRef.current) {
          const bufferDuration = Date.now() - bufferStartTimeRef.current;

          const { baseURI, title } = videoRef.current;
          if (!isThumbnail) {
            track({
              event: "Video Buffering Completed",
              properties: {
                video_content_id: baseURI,
                video_content_title: title,
                video_position: videoPosition,
                buffer_duration: bufferDuration,
                in_view: inViewRef.current
              }
            });
          }
          bufferStartTimeRef.current = null;
        }
      });
      player.on("playing", () => {
        isProgrammaticPause.current = false;
      });

      const onVolumeChange = debounce(() => {
        // do not track if the video is muted programmatically at the start
        if (isAutoMuted.current === true || !videoRef.current) return;
        const { baseURI, title } = videoRef.current;
        const volume = player.muted() ? 0 : player.volume();
        if (!isThumbnail) {
          track({
            event: "Video Volume Changed",
            properties: {
              video_content_id: baseURI,
              video_content_title: title,
              video_position: videoPosition,
              volume
            }
          });
        }
        if (isProgrammaticPause.current && !isThumbnail) {
          track({
            event: "Page Element Clicked",
            properties: {
              location_type: `${videoPosition} Video`,
              module_content: "Volume",
              module_content_details: volume === 0 ? "Muted" : "Unmuted"
            }
          });
        }
      }, 250);

      player.on("volumechange", onVolumeChange);

      player.on("fullscreenchange", () => {
        if (player.isFullscreen()) {
          track({
            event: "Page Element Clicked",
            properties: {
              location_type: `${videoPosition} Video`,
              module_content: "Fullscreen",
              module_content_details: "Entered"
            }
          });
        } else {
          track({
            event: "Page Element Clicked",
            properties: {
              location_type: `${videoPosition} Video`,
              module_content: "Fullscreen",
              module_content_details: "Exited"
            }
          });
        }
      });

      player.on("enterpictureinpicture", () => {
        track({
          event: "Page Element Clicked",
          properties: {
            location_type: `${videoPosition} Video`,
            module_content: "Picture-in-Picture",
            module_content_details: "Entered"
          }
        });
      });

      player.on("leavepictureinpicture", () => {
        track({
          event: "Page Element Clicked",
          properties: {
            location_type: `${videoPosition} Video`,
            module_content: "Picture-in-Picture",
            module_content_details: "Exited"
          }
        });
      });

      player.on("seeked", () => {
        if (videoRef.current) {
          const { duration, currentTime } = videoRef.current;
          const percentage = (currentTime / duration) * 100;
          if (!isThumbnail) {
            track({
              event: "Page Element Clicked",
              properties: {
                location_type: `${videoPosition} Video`,
                module_content: "Progress bar",
                module_content_details: `${percentage.toFixed(2)}%`
              }
            });
          }
        }
      });

      const onError = (playbackError) => {
        setError(playbackError);
        setVideoLoaded(false);
        setCoverImageHidden(false);
        track({
          event: "Video Playback Failed",
          properties: {
            video_content_id: videoRef.current?.baseURI ?? "",
            total_length: videoRef.current?.duration || 0,
            video_content_title: videoRef.current?.title ?? "",
            video_position: videoPosition,
            error: playbackError.target.innerText
          }
        });
      };
      player.on("error", onError);

      setVideoLoaded(true);
    };

    loadVideo();
    return () => player?.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const player = videoPlayerRef.current;
    if (!player || !isReady || player.isDisposed()) return undefined;

    player.muted(muted);

    if (
      autoPlayWhenScrolledIntoView &&
      inView &&
      !isProgrammaticPause.current
    ) {
      player.play();
    } else if (pauseWhenNotInView && !inView) {
      player.pause();
    }
  }, [
    isReady,
    muted,
    autoPlayWhenScrolledIntoView,
    pauseWhenNotInView,
    inView,
    videoPlayerRef
  ]);

  useEffect(() => {
    const player = videoPlayerRef.current;
    if (preloadInView && isReady && player && !player.isDisposed()) {
      if (player.preload() !== "auto") {
        player.preload(true);
        player.load();
      }
    }
  }, [preloadInView, isReady, videoPlayerRef]);

  const schema = {
    "@context": "https://schema.org/",
    "@type": "VideoObject",
    name: videoProps?.title,
    description,
    thumbnailUrl: coverImageProps.src,
    duration: videoRef?.current?.duration,
    "@id": videoRef.current?.src
  };
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema)
          }}
        />
      </Head>
      {!error && (
        <Box
          {...otherProps}
          position="relative"
          display={isVideoLoaded ? "block" : "none"}
          className={videoStyles}
          ref={ref}
        >
          <div data-vjs-player>
            <video
              ref={videoRef}
              crossOrigin="anonymous"
              preload="metadata"
              onPlay={() => {
                isAutoMuted.current = false;

                if (
                  videoRef.current &&
                  isProgrammaticPause.current &&
                  !isThumbnail
                ) {
                  track({
                    event: "Page Element Clicked",
                    properties: {
                      location_type: `${videoPosition} Video`,
                      module_content: "Play button",
                      module_content_details: "play"
                    }
                  });
                }

                isProgrammaticPause.current = false;
                if (!isCoverImageHidden) setCoverImageHidden(true);
                if (!isThumbnail) {
                  trackVideo("play");
                }

                if (videoRef.current && inView && !isThumbnail) {
                  const { duration, baseURI, title } = videoRef.current;
                  track({
                    event: "Video Playback Started",
                    properties: {
                      total_length: removeNullField(duration),
                      video_content_id: baseURI,
                      video_content_title: title,
                      video_position: videoPosition
                    }
                  });
                }
              }}
              onError={() => setCoverImageHidden(false)}
              onPause={() => {
                if (!isThumbnail) {
                  trackVideo("pause");
                }
                if (inView) {
                  isProgrammaticPause.current = true;
                }
                if (videoRef.current) {
                  const { duration, currentTime, baseURI, title } =
                    videoRef.current;
                  const remaining = duration - currentTime;
                  if (remaining >= 0.05 && !isThumbnail) {
                    track({
                      event: "Video Playback Paused",
                      properties: {
                        total_length: removeNullField(duration),
                        video_content_id: baseURI,
                        video_content_title: title,
                        video_position: videoPosition
                      }
                    });
                    track({
                      event: "Page Element Clicked",
                      properties: {
                        location_type: `${videoPosition} Video`,
                        module_content: "Pause button",
                        module_content_details: "pause"
                      }
                    });
                  }
                }
              }}
              onEnded={() => {
                if (!isThumbnail) {
                  trackVideo("completed");
                }
                if (videoRef.current && !isThumbnail) {
                  const { duration, baseURI, title } = videoRef.current;
                  track({
                    event: "Video Playback Completed",
                    properties: {
                      total_length: removeNullField(duration),
                      video_content_id: baseURI,
                      video_content_title: title,
                      video_position: videoPosition
                    }
                  });
                }
              }}
              className="video-js vjs-big-play-centered"
              {...videoProps}
              style={{ objectFit: "cover", overflow: "hidden" }}
            />

            {!isCoverImageHidden && (
              <Poster
                imgProps={coverImageProps}
                {...coverImageContainerProps}
              />
            )}
          </div>
        </Box>
      )}

      {/* show this placeholder until the video is ready to play */}
      {!isVideoLoaded && isPlaceholderEnabled && (
        <Box
          {...otherProps}
          position="relative"
          css={{
            "&:after": {
              content: "''",
              display: "block",
              paddingBottom: "100%" // 1/1 aspect ratio
            }
          }}
        >
          <Poster imgProps={coverImageProps} {...coverImageContainerProps} />
        </Box>
      )}
    </>
  );
};

export const VideoPlayer = withErrorBoundary(VideoPlayerComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`VideoPlayer failed to load. ${error}`);
  }
});
